import React, { FC, useCallback, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '@/ui-kit/hooks/useIsDesktop';
import { useDoVerify } from './hooks/useDoVerify';
import { VerifyCodeState } from '../../types';
import { PageTitle } from '@/ui-kit/components';
import { useDoReSendUserVerify } from '@/bo-kit/components/VerifyAccountPage/components/VerifyAccountForm/hooks/useDoReSendUserVerify';
import { EmailOTPCodeForm } from '@/bo-kit/components/EmailOTPCodeForm';
import { VerifyRequest } from '../../../../transport/types/VerifyRequest';

export const VerifyCodeForm: FC<VerifyCodeState> = ({ id, email, code, verifyOnLogin = false }) => {
    const { t } = useTranslation('bo-kit__signup');
    const { t: validationT } = useTranslation('bo-kit__validation');
    const { isLoading, error, doVerify, errorMessage } = useDoVerify();
    const [_doResendCode] = useDoReSendUserVerify();

    const doResendCode = useCallback(() => _doResendCode(id), [_doResendCode, id]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                confirmationCode: Yup.string()
                    .required(validationT('Field is required'))
                    .matches(/^[0-9]*/, validationT('verificationCodeInvalid')),
            }),
        [validationT]
    );
    const isDesktop = useIsDesktop();

    return (
        <Formik<VerifyRequest>
            initialValues={{ confirmationCode: code ?? '', userId: id }}
            onSubmit={doVerify}
            validationSchema={validationSchema}
        >
            {(formik) => (
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={{ maxWidth: isDesktop ? 300 : 'unset' }}>
                        <PageTitle>{t('Verification')}</PageTitle>
                        <Stack spacing={4}>
                            {verifyOnLogin && (
                                <Typography variant='h4' color='common.black'>
                                    {t(
                                        "Unfortunately, your email wasn't verified yet. Pls, verify it to proceed with sign in."
                                    )}
                                </Typography>
                            )}
                            <EmailOTPCodeForm
                                email={email}
                                isError={!!error}
                                doResendCode={doResendCode}
                                formik={formik}
                                isLoading={isLoading}
                                errorMessage={errorMessage}
                                submitBtnCaption={t('verifyBtn')}
                            />
                        </Stack>
                    </Box>
                </form>
            )}
        </Formik>
    );
};
